//---------------------------------------------------------------------------------------------------
// ## IMPORTS ##
// Library Imports
import { useEffect, useState } from "react";

// Custom components imports (and hooks and helpers)
import { useAppDispatch, useAppSelector } from "../../store/hooks";

import { Grid, Typography, useTheme } from "@mui/material";

import { Params, useNavigate, useParams } from "react-router-dom";
import { useCreateSceneMutation, useGetScenesQuery } from "../../services/scene.service";
import { EDraggableOptions } from "@virtus-tech-repository/virtus-tech-repository/lib/models/media.model";
import { IScene } from "@virtus-tech-repository/virtus-tech-repository/lib/models/scenario.model";

import { VTTypography } from "@virtus-tech-repository/virtus-tech-repository";

import { LineChart, PieChart } from "@mui/x-charts";
import { ITrackingActions, ITrackingHotspotAction } from "../../models/tracking.models";
import { useGetHotspotsQuery } from "../../services/hotspot.service";
import { IHotspot } from "@virtus-tech-repository/virtus-tech-repository/lib/models/hotspot.model";
import VTTimelineItem from "@virtus-tech-repository/virtus-tech-repository/lib/components/VTTimelineItem";
import { getImageForSceneOrDefaultImageIfPreviewImageDoesntExist } from "../../utils/getImageForSceneOrDefaultImageIfPreviewImageDoesntExist";
import { useGetScenarioOverviewQuery } from "../../services/scenario.service";
import ViewMarksContainerTable from "./MarksViewContainerTable";
import NoUser from "../../assets/images/NoScenarios.svg";

interface IProps {
    drawWidth: number;
}
//---------------------------------------------------------------------------------------------------
// ## COMPONENTS ##
// Error Page, to display on breaking errors and allows home navigation
export default function VTOverviewUser({ drawWidth }: IProps) {
    //-------------------------------------------------------------------------------------------------
    // ## USE SELECTOR ##
    const theme = useTheme();
    const [currentSceneOverview, setCurrentSceneOverview] = useState<string>("");
    const { selectedAttempt } = useAppSelector((state) => state.currentReducer);
    //-------------------------------------------------------------------------------------------------
    // ## USE STATE ##

    //-------------------------------------------------------------------------------------------------
    // ## HOOKS ##
    const { scenarioId, sceneId }: Readonly<Params<string>> = useParams();
    const { data: scenarioOverviewData } = useGetScenarioOverviewQuery(scenarioId ? scenarioId : "");

    const [hotspotClicked, setHotspotClicked] = useState<number>(0);
    const [advTimePerScene, setAdvTimePerScene] = useState<number>(0);
    const [scenesLogged, setScenesLogged] = useState<
        { sceneId: string; timeOnScene: number; hotspotsClicked: number; hotspotsHovered: number }[]
    >([]);

    useEffect(() => {
        if (selectedAttempt) {
            let tempHotspotsClicked = 0;
            let tempSceneTime = 0;
            let tempScenesLogged: {
                sceneId: string;
                timeOnScene: number;
                hotspotsClicked: number;
                hotspotsHovered: number;
            }[] = [];

            selectedAttempt.data.actions.map((sceneElement: any) => {
                if (sceneElement.hotspotActions.length) {
                    const sceneElementTime =
                        Math.abs(
                            new Date(
                                sceneElement.hotspotActions[sceneElement.hotspotActions.length - 1].date,
                            ).getTime() - new Date(sceneElement.date).getTime(),
                        ) / 1000;
                    let sceneElementHotspotClicked = 0;
                    let sceneElementHotspotHovered: string[] = [];

                    tempSceneTime = tempSceneTime + sceneElementTime;

                    sceneElement.hotspotActions.map((actionElement: any) => {
                        if (actionElement.action === "Clicked") {
                            sceneElementHotspotClicked++;
                        }

                        if (
                            actionElement.action === "Hovered" &&
                            !sceneElementHotspotHovered.includes(actionElement.hotspotId)
                        ) {
                            sceneElementHotspotHovered.push(actionElement.hotspotId);
                        }
                    });

                    tempHotspotsClicked = tempHotspotsClicked + sceneElementHotspotClicked;

                    if (
                        tempScenesLogged.filter((filterElement: any) => filterElement.sceneId === sceneElement.sceneId)
                    ) {
                        tempScenesLogged.push({
                            sceneId: sceneElement.sceneId,
                            timeOnScene: sceneElementTime,
                            hotspotsClicked: sceneElementHotspotClicked,
                            hotspotsHovered: sceneElementHotspotHovered.length,
                        });
                    } else {
                        let tempIndex = tempScenesLogged.findIndex(
                            (indexFindElement: any) => indexFindElement.sceneId === sceneElement.sceneId,
                        );
                        tempScenesLogged[tempIndex] = {
                            sceneId: sceneElement.sceneId,
                            timeOnScene: tempScenesLogged[tempIndex].timeOnScene + sceneElementTime,
                            hotspotsClicked: sceneElementHotspotClicked,
                            hotspotsHovered: sceneElementHotspotHovered.length,
                        };
                    }
                }
            });

            setAdvTimePerScene(Math.round(tempSceneTime / selectedAttempt.data.actions.length));
            setHotspotClicked(tempHotspotsClicked);
            setScenesLogged(tempScenesLogged);

            if (scenarioOverviewData && scenarioOverviewData.scenes) {
                setCurrentSceneOverview(
                    [...scenarioOverviewData.scenes]?.sort((element1: IScene, element2: IScene) => {
                        if (element1.order === undefined) {
                            return 1;
                        } else if (element2.order === undefined) {
                            return -1;
                        } else {
                            return element2.order < element1.order ? 1 : -1;
                        }
                    })[0].id,
                );
            }
        }
    }, [selectedAttempt]);

    //-------------------------------------------------------------------------------------------------
    // ## RETURN ##
    // Displays return button and error message
    return (
        <>
            {selectedAttempt && (
                <Grid container justifyContent={"space-evenly"}>
                    <Grid item container xs={9} sx={{ height: `calc(100vh - 130px - ${drawWidth}px)` }}>
                        <Grid
                            xs={6}
                            item
                            container
                            justifyContent={"space-evenly"}
                            alignContent={"space-evenly"}
                            sx={{ height: `calc(50vh -  ${drawWidth}px)` }}
                        >
                            <Grid
                                item
                                container
                                sx={{
                                    backgroundColor: "#393939",
                                    borderRadius: "15px",
                                }}
                            >
                                <VTTypography sx={{ marginTop: "10px", marginLeft: "10px" }}>
                                    Activity per scene
                                </VTTypography>
                                {selectedAttempt && selectedAttempt.data && (
                                    <LineChart
                                        series={[
                                            {
                                                data: [
                                                    ...selectedAttempt.data.actions.map(
                                                        (attemptAction: any) =>
                                                            attemptAction.hotspotActions.filter(
                                                                (filterElement: ITrackingHotspotAction) =>
                                                                    filterElement.action !== "Hovered",
                                                            ).length + 1,
                                                    ),
                                                ],
                                                area: true,
                                            },
                                        ]}
                                        sx={{ width: "100%" }}
                                        height={window.document.body.offsetHeight / 2 - 42.5 - drawWidth}
                                    />
                                )}
                            </Grid>
                        </Grid>
                        <Grid
                            xs={6}
                            item
                            container
                            justifyContent={"space-around"}
                            alignContent={"space-evenly"}
                            sx={{ height: `calc(50vh  - ${drawWidth}px)`, padding: "10px" }}
                        >
                            <Grid
                                item
                                xs={5}
                                sx={{ backgroundColor: "#393939", borderRadius: "15px", height: "50%" }}
                                container
                                justifyContent={"center"}
                                alignContent={"center"}
                                direction={"column"}
                            >
                                <Grid item>
                                    <VTTypography align="center">Total Session time</VTTypography>
                                </Grid>
                                <Grid item>
                                    <VTTypography align="center" type={"Heading3"}>
                                        {Math.floor(
                                            Math.round(
                                                Math.abs(
                                                    new Date(
                                                        selectedAttempt.data.actions[
                                                            selectedAttempt.data.actions.length - 1
                                                        ].date,
                                                    ).getTime() - new Date(selectedAttempt.data.date).getTime(),
                                                ) / 1000,
                                            ) / 60,
                                        )}
                                        m{" "}
                                        {Math.round(
                                            Math.abs(
                                                new Date(
                                                    selectedAttempt.data.actions[
                                                        selectedAttempt.data.actions.length - 1
                                                    ].date,
                                                ).getTime() - new Date(selectedAttempt.data.date).getTime(),
                                            ) / 1000,
                                        ) % 60}
                                        s
                                    </VTTypography>
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                xs={5}
                                sx={{ backgroundColor: "#393939", borderRadius: "15px", height: "50%" }}
                                container
                                justifyContent={"center"}
                                alignContent={"center"}
                                direction={"column"}
                            >
                                <Grid item>
                                    <VTTypography align="center">Hotspots Clicked</VTTypography>
                                </Grid>
                                <Grid item>
                                    <VTTypography align="center" type={"Heading3"}>
                                        {hotspotClicked}
                                    </VTTypography>
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                xs={5}
                                sx={{
                                    backgroundColor: "#393939",
                                    borderRadius: "15px",
                                    height: "50%",
                                    marginTop: "15px",
                                }}
                                container
                                justifyContent={"center"}
                                alignContent={"center"}
                                direction={"column"}
                            >
                                <Grid item>
                                    <VTTypography align="center">Adv time per scene</VTTypography>
                                </Grid>
                                <Grid item>
                                    <VTTypography align="center" type={"Heading3"}>
                                        {Math.floor(Math.round(advTimePerScene / 60))}m{" "}
                                        {Math.round(advTimePerScene % 60)}s
                                    </VTTypography>
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                xs={5}
                                sx={{
                                    backgroundColor: "#393939",
                                    borderRadius: "15px",
                                    height: "50%",
                                    marginTop: "15px",
                                }}
                                container
                                justifyContent={"center"}
                                alignContent={"center"}
                                direction={"column"}
                            >
                                <Grid item>
                                    <VTTypography align="center">Scenes viewed</VTTypography>
                                </Grid>
                                <Grid item>
                                    <VTTypography align="center" type={"Heading3"}>
                                        {
                                            scenesLogged.filter(
                                                (
                                                    filterElement: { sceneId: string; timeOnScene: number },
                                                    filterIndex: number,
                                                ) =>
                                                    filterIndex ===
                                                    scenesLogged.findIndex(
                                                        (findElement: { sceneId: string; timeOnScene: number }) =>
                                                            findElement.sceneId === filterElement.sceneId,
                                                    ),
                                            ).length
                                        }
                                        {scenarioOverviewData &&
                                            scenarioOverviewData.scenes &&
                                            "/" + scenarioOverviewData.scenes.length}
                                    </VTTypography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid
                            xs={12}
                            item
                            container
                            sx={{
                                height: `calc(50vh - 5px - ${drawWidth}px)`,
                                padding: "10px",
                            }}
                        >
                            <Grid
                                container
                                sx={{
                                    height: "100%",
                                    backgroundColor: "#393939",
                                    borderRadius: "15px",
                                    overflowY: "auto",
                                }}
                                justifyContent={"center"}
                                xs={4}
                            >
                                <VTTypography
                                    sx={{ marginLeft: "10px", marginTop: "3px", width: "100%", marginBottom: "10px" }}
                                    align="left"
                                >
                                    Scene
                                </VTTypography>

                                {scenarioOverviewData &&
                                    scenarioOverviewData.scenes &&
                                    [...scenarioOverviewData.scenes]
                                        ?.sort((element1: IScene, element2: IScene) => {
                                            if (element1.order === undefined) {
                                                return 1;
                                            } else if (element2.order === undefined) {
                                                return -1;
                                            } else {
                                                return element2.order < element1.order ? 1 : -1;
                                            }
                                        })
                                        .map((sceneElement: IScene) => (
                                            <Grid
                                                item
                                                xs={12}
                                                container
                                                justifyContent={"center"}
                                                sx={{ marginBottom: "10px" }}
                                            >
                                                <VTTimelineItem
                                                    mediaType={sceneElement.mediaType}
                                                    image={getImageForSceneOrDefaultImageIfPreviewImageDoesntExist(
                                                        sceneElement,
                                                    )}
                                                    name={sceneElement.name}
                                                    disableHover
                                                    active={currentSceneOverview === sceneElement.id}
                                                    onClicked={() => {
                                                        setCurrentSceneOverview(sceneElement.id);
                                                    }}
                                                    sx={{ width: "90%" }}
                                                />
                                            </Grid>
                                        ))}
                            </Grid>
                            <Grid item container sx={{ paddingLeft: "10px" }} xs={8}>
                                <Grid item xs={12} sx={{ borderBottom: "2px solid white", height: "35px" }}>
                                    <VTTypography type={"Heading3"}>Scene Data</VTTypography>
                                </Grid>

                                <Grid
                                    xs={currentSceneOverview !== "" ? 12 : 1}
                                    item
                                    container
                                    justifyContent={"space-evenly"}
                                    sx={
                                        currentSceneOverview !== ""
                                            ? {
                                                  height: "100%",
                                                  transition: "0.5s",
                                                  zIndex: 10,
                                                  paddingTop: "20px",
                                              }
                                            : {
                                                  height: "100%",
                                                  transition: "0.5s",
                                                  zIndex: -10,
                                                  transform: "translate(-90px)",
                                              }
                                    }
                                >
                                    {currentSceneOverview !== "" ? (
                                        <>
                                            <Grid
                                                item
                                                xs={3}
                                                sx={{
                                                    backgroundColor: "#393939",
                                                    borderRadius: "15px",
                                                    height: `calc(50vh - 80px - ${drawWidth}px)`,
                                                }}
                                                container
                                                justifyContent={"center"}
                                                alignContent={"center"}
                                                direction={"column"}
                                            >
                                                <Grid item>
                                                    <VTTypography align="center">Time on Scene</VTTypography>
                                                </Grid>
                                                <Grid item>
                                                    <VTTypography align="center" type={"Heading3"}>
                                                        {Math.round(
                                                            scenesLogged.find(
                                                                (findElement: {
                                                                    sceneId: string;
                                                                    timeOnScene: number;
                                                                }) => findElement.sceneId === currentSceneOverview,
                                                            )?.timeOnScene!,
                                                        )}
                                                        s
                                                    </VTTypography>
                                                </Grid>
                                            </Grid>
                                            <Grid
                                                item
                                                xs={3}
                                                sx={{
                                                    backgroundColor: "#393939",
                                                    borderRadius: "15px",
                                                    height: `calc(50vh - 80px - ${drawWidth}px)`,
                                                }}
                                                container
                                                justifyContent={"center"}
                                                alignContent={"center"}
                                                direction={"column"}
                                            >
                                                <Grid item>
                                                    <VTTypography align="center">Hotspots Clicked</VTTypography>
                                                </Grid>
                                                <Grid item>
                                                    <VTTypography align="center" type={"Heading3"}>
                                                        {
                                                            scenesLogged.find(
                                                                (findElement: {
                                                                    sceneId: string;
                                                                    timeOnScene: number;
                                                                }) => findElement.sceneId === currentSceneOverview,
                                                            )?.hotspotsClicked!
                                                        }
                                                    </VTTypography>
                                                </Grid>
                                            </Grid>
                                            <Grid
                                                item
                                                xs={3}
                                                sx={{
                                                    backgroundColor: "#393939",
                                                    borderRadius: "15px",
                                                    height: `calc(50vh - 80px - ${drawWidth}px)`,
                                                }}
                                                container
                                                justifyContent={"center"}
                                                alignContent={"center"}
                                                direction={"column"}
                                            >
                                                <Grid item>
                                                    <VTTypography align="center">Hotspots Hovered</VTTypography>
                                                </Grid>
                                                <Grid item>
                                                    <VTTypography align="center" type={"Heading3"}>
                                                        {
                                                            scenesLogged.find(
                                                                (findElement: {
                                                                    sceneId: string;
                                                                    timeOnScene: number;
                                                                }) => findElement.sceneId === currentSceneOverview,
                                                            )?.hotspotsHovered!
                                                        }
                                                    </VTTypography>
                                                </Grid>
                                            </Grid>
                                        </>
                                    ) : (
                                        <>
                                            <Grid item xs={12} container justifyContent={"center"}>
                                                <VTTypography type={"Heading3"} sx={{ marginTop: "30px" }}>
                                                    No scene selected
                                                </VTTypography>
                                            </Grid>

                                            <Grid item xs={12} container justifyContent={"center"}>
                                                <VTTypography type={"Heading4"}>
                                                    Please select a scene from the panel on the left
                                                </VTTypography>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        xs={3}
                        sx={{
                            height: `calc(100vh - 145px - ${drawWidth}px)`,
                            overflow: "auto",
                            backgroundColor: "#393939",
                            borderRadius: "15px",
                            padding: "10px",
                            marginTop: "3px",
                        }}
                    >
                        <ViewMarksContainerTable drawWidth={drawWidth} />
                    </Grid>
                </Grid>
            )}
        </>
    );
}
